import { useEffect, useMemo } from 'react';

import { useMyStatusQuery } from '@/entities/me';

import type { PatientNavigatorItemData } from './types';
import type { ScopedPatientTab, ScopedPatientTabOption } from './scoped-tabs';
import {
  buildScopedPatientTabs,
  filterPatientsByScopedTab,
  resolveScopedPatientTab,
} from './scoped-tabs';

interface UseScopedPatientTabsParams {
  patients: PatientNavigatorItemData[];
  activeTab: ScopedPatientTab;
  isPatientsLoading: boolean;
  onTabChange: (tab: ScopedPatientTab) => void;
}

interface UseScopedPatientTabsResult {
  availableTabs: ScopedPatientTabOption[];
  effectiveTab: ScopedPatientTab | null;
  filtered: PatientNavigatorItemData[];
}

/**
 * Shared hook that encapsulates scoped patient tab resolution.
 *
 * Used by both PatientNavigator (workspace sidebar) and PatientQueue
 * (dashboard) so the auto-resolution + parent-propagation logic lives
 * in a single place.
 */
export function useScopedPatientTabs({
  patients,
  activeTab,
  isPatientsLoading,
  onTabChange,
}: UseScopedPatientTabsParams): UseScopedPatientTabsResult {
  const { data: myStatus, isPending: isStatusPending } = useMyStatusQuery();
  const isEvaluationAdmin = myStatus?.subsystemAccess?.['evaluation_admin'] === true;

  const availableTabs = useMemo(
    () => buildScopedPatientTabs(patients, isEvaluationAdmin),
    [patients, isEvaluationAdmin]
  );

  const effectiveTab = useMemo(
    () => resolveScopedPatientTab(activeTab, availableTabs),
    [activeTab, availableTabs]
  );

  useEffect(() => {
    if (isPatientsLoading || isStatusPending) {
      return;
    }
    if (effectiveTab === null || effectiveTab === activeTab) {
      return;
    }
    onTabChange(effectiveTab);
  }, [activeTab, effectiveTab, isPatientsLoading, isStatusPending, onTabChange]);

  const filtered = useMemo(
    () => (effectiveTab === null ? [] : filterPatientsByScopedTab(patients, effectiveTab)),
    [effectiveTab, patients]
  );

  return { availableTabs, effectiveTab, filtered };
}
